// utils/logSanitiser.ts
//
// Browser copy of server/src/utils/logSanitiser.ts — keep the rule sets in
// sync with the server module. Browser errors are untrusted at the central
// ingest, but sanitisation still applies: stored records must never carry
// secrets or raw clinical payload data regardless of trust level.
//
// Deterministic sanitisation for telemetry payloads before they leave the
// process (central error ingest, and later AI analysis). Applied centrally
// inside the logging functions so every call site gets the same protection.
//
// Rules follow docs/Error Logging Improvements.md §3:
// - credentials/secrets hard-redacted ("[REDACTED]")
// - PII masked to a partially-useful form (email domain, phone last-3)
// - structural bounds so telemetry cannot amplify memory/transport

const REDACTED = '[REDACTED]';

const MAX_DEPTH = 6;
const MAX_ARRAY_LENGTH = 20;
const MAX_OBJECT_KEYS = 50;
const MAX_STRING_LENGTH = 2000;
export const MAX_METADATA_BYTES = 32768;

// Key-name denylist — values under these keys are credentials regardless of
// their shape. Matched against the key only (case-insensitive, separator-
// tolerant) so variants like `apiKey`, `api_key`, `x-api-key` all redact.
const SECRET_KEY_PATTERN =
  /(pass(word|wd)?|secret|token|jwt|authoriz|api[-_]?key|cookie|credential|private[-_]?key|client[-_]?secret|cvv|cvc|cv2|card[-_]?number|security[-_]?code|\bpin\b|totp|mfa|otp|challenge|session[-_]?id|connection[-_]?string|bearer|x[-_]?application[-_]?token)/i;

// `number`/`expiry`/`cvv`-style keys are only secrets when they sit under a
// card/payment/billing parent — elsewhere `number` may be an order quantity.
const CARD_CONTEXT_PATTERN = /(card|payment|billing|stripe)/i;
const CARD_FIELD_PATTERN =
  /^(number|expiry|expires|exp|exp_month|exp_year|month|year|cvv|cvc|cv2|sort[-_]?code|account[-_]?number|iban|bic)$/i;

// Phones are matched by key suffix — bare digit strings are too ambiguous to
// pattern-match (order references, timestamps, IDs).
const PHONE_KEY_PATTERN = /(phone|mobile|telephone|tel)(number|no)?$/i;

const EMAIL_PATTERN = /\b[A-Za-z0-9._%+-]+@([A-Za-z0-9-]+\.)+[A-Za-z]{2,}\b/g;

// Free-text credential patterns applied to every emitted string — error
// messages, stacks and third-party responses may embed secrets.
const FREETEXT_REPLACERS: Array<[RegExp, string | ((m: string) => string)]> = [
  // Authorization-style headers: "Bearer eyJ...", "Basic dXNlcg=="
  [/\b(Bearer|Basic)\s+[A-Za-z0-9._~+/=-]{6,}/gi, `$1 ${REDACTED}`],
  // JWTs
  [/\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{4,}\b/g, `${REDACTED}_JWT`],
  // Connection-string credentials: mongodb://user:pass@host
  [/\b(mongodb(?:\+srv)?|postgres(?:ql)?|mysql|redis|amqp):\/\/[^\s:@/]+:[^\s@/]+@/gi, `$1://${REDACTED}@`],
  // Provider key formats (Stripe sk_/pk_/rk_/whsec_, Slack xox*, etc.)
  [/\b(sk|pk|rk|whsec|xox[baprs])_(live|test)?_?[A-Za-z0-9]{10,}\b/g, `${REDACTED}_KEY`],
  // key=value / key:value credential pairs embedded in text or JSON strings
  [
    /((?:pass(?:word|wd)?|secret|api[_-]?key|access[_-]?key|auth[_-]?token|session[_-]?token|token|jwt|cookie|credential)\s*["']?\s*[=:]\s*["']?)([^\s"',}&]+)/gi,
    `$1${REDACTED}`
  ],
  // Emails — masked by value pattern, keeping the domain for diagnostics
  [EMAIL_PATTERN, (m: string) => `***@${m.split('@')[1]}`]
];

export function sanitizeText(value: string): string {
  let out = value;
  for (const [pattern, replacement] of FREETEXT_REPLACERS) {
    out = out.replace(pattern as RegExp, replacement as string);
  }
  return out;
}

function maskPhone(value: unknown): string {
  const digits = String(value ?? '').replace(/\D/g, '');
  return digits.length >= 3 ? `***${digits.slice(-3)}` : '[MASKED]';
}

function maskEmail(value: string): string {
  return value.includes('@') ? `***@${value.split('@')[1]}` : '[MASKED]';
}

function reduceError(err: Error): Record<string, unknown> {
  const out: Record<string, unknown> = {
    name: err.name,
    message: sanitizeText(err.message || '')
  };
  const code = (err as any).code;
  if (code !== undefined) out.code = code;
  if (err.stack) out.stack = truncate(sanitizeText(err.stack), 8000);
  return out;
}

function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max)}…[truncated]` : value;
}

function sanitizeValue(value: unknown, depth: number, key: string, parentKey: string): unknown {
  if (SECRET_KEY_PATTERN.test(key)) return REDACTED;
  if (CARD_CONTEXT_PATTERN.test(parentKey) && CARD_FIELD_PATTERN.test(key)) return REDACTED;
  if (PHONE_KEY_PATTERN.test(key)) return maskPhone(value);

  if (value === null || value === undefined) return value;
  if (typeof value === 'boolean' || typeof value === 'number') return value;
  if (typeof value === 'bigint' || typeof value === 'symbol' || typeof value === 'function') {
    return String(value);
  }
  if (value instanceof Error) return depth >= MAX_DEPTH ? '[Error]' : reduceError(value);
  if (value instanceof Date) return value.toISOString();

  if (typeof value === 'string') {
    // Emails as whole values keep the domain like free-text masking
    if (/^[A-Za-z0-9._%+-]+@([A-Za-z0-9-]+\.)+[A-Za-z]{2,}$/.test(value)) return maskEmail(value);
    return truncate(sanitizeText(value), MAX_STRING_LENGTH);
  }

  if (depth >= MAX_DEPTH) return '[DEPTH_LIMIT]';

  if (Array.isArray(value)) {
    const out = value.slice(0, MAX_ARRAY_LENGTH).map((item) => sanitizeValue(item, depth + 1, '', key));
    if (value.length > MAX_ARRAY_LENGTH) out.push(`…+${value.length - MAX_ARRAY_LENGTH} more`);
    return out;
  }

  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    const keys = Object.keys(value as object).slice(0, MAX_OBJECT_KEYS);
    for (const k of keys) {
      out[k] = sanitizeValue((value as Record<string, unknown>)[k], depth + 1, k, key);
    }
    if (Object.keys(value as object).length > MAX_OBJECT_KEYS) out.__truncatedKeys = true;
    return out;
  }

  return String(value);
}

// Top-level object sanitiser + serialised-size bound (mirrors the central
// ingest limit so nothing we send is silently truncated server-side).
export function sanitizePayload(value: unknown): Record<string, unknown> {
  if (value === undefined || value === null) return {};
  const sanitised = sanitizeValue(value, 0, '', '');
  try {
    const bytes = JSON.stringify(sanitised).length;
    if (bytes > MAX_METADATA_BYTES) return { truncated: true, bytes };
  } catch {
    return { truncated: true, bytes: -1 };
  }
  return sanitised as Record<string, unknown>;
}

// Keep the route path for correlation but strip query strings/fragments —
// tokens and credentials commonly live there (?token=, ?key=).
export function sanitizePath(path: unknown): string | undefined {
  if (typeof path !== 'string' || !path) return undefined;
  const clean = path.split(/[?#]/)[0];
  return truncate(clean, 500);
}

// Correlation IDs are untrusted input — accepted only if conservative.
export const REQUEST_ID_PATTERN = /^[A-Za-z0-9_-]{8,64}$/;

export function isValidRequestId(value: unknown): value is string {
  return typeof value === 'string' && REQUEST_ID_PATTERN.test(value);
}

// Logger-side re-validation (§3a): internal callers bypass middleware, so a
// bad ID is dropped rather than trusted or allowed to break the emit.
export function sanitizeRequestId(value: unknown): string | undefined {
  if (!isValidRequestId(value)) return undefined;
  return value.slice(0, 128);
}

export function sanitizeMessage(value: unknown): string {
  return truncate(sanitizeText(String(value ?? '')), 4000);
}

export function sanitizeStack(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value) return undefined;
  return truncate(sanitizeText(value), 50000);
}

export function sanitizeStatusCode(value: unknown): number | undefined {
  return Number.isInteger(value) && (value as number) >= 100 && (value as number) <= 599
    ? (value as number)
    : undefined;
}

const ALLOWED_CATEGORIES = new Set(['server', 'client', 'network', 'auth', 'database', 'integration']);
export function sanitizeCategory(value: unknown): string {
  return typeof value === 'string' && ALLOWED_CATEGORIES.has(value) ? value : 'server';
}

export function boundedString(value: unknown, max = 200): string | undefined {
  if (typeof value !== 'string' || !value) return undefined;
  return truncate(sanitizeText(value), max);
}
